import { FormattedMessage, useIntl } from 'react-intl';
import { Field } from 'formik';
import Heading from '../../../common/Heading';
import formStyles from '../../../styles/forms.module.scss';
import s from './StepGreenPowerData.module.scss';
import {
  TextInput,
  RadioGroupInput,
  AccessibleDatepickerInput,
} from '../../../common/FormFields';
import InfoBox from '../../../common/InfoBox/InfoBox';
import {
  getControlAreaRadioOptions,
  getGreenGreyPowerProductOptions,
  isCustomControlArea,
  type GreyPowerField,
} from '../../../domain/facility';
import type { WizardStepProps } from '../wizard/types/wizardTypes';

const NAME_PREFIX = 'greyPowerData.';

const POWER_CONTROL_AREA_OTHER_MSG_ID =
  'offerWizard.stepGreenPowerData.controlArea.other';

type StepGreyPowerDataProps = Pick<
  WizardStepProps,
  'values' | 'isGreyPowerConsumptionTooSmall'
>;

function StepGreyPowerData({
  values,
  isGreyPowerConsumptionTooSmall,
}: StepGreyPowerDataProps) {
  const intl = useIntl();
  const fieldName = (field: GreyPowerField) => `${NAME_PREFIX}${field}`;

  const isControlAreaCustom = isCustomControlArea(
    values.greyPowerData?.controlArea
  );

  const linkToVerbundProductPortfolio =
    intl.locale === 'en'
      ? 'https://www.verbund.com/en-at'
      : 'https://www.verbund.com/de-at';

  const controlAreaOptions = getControlAreaRadioOptions(
    intl,
    POWER_CONTROL_AREA_OTHER_MSG_ID
  );
  const productOptions = getGreenGreyPowerProductOptions();

  return (
    <>
      <Heading level={2} className={s.heading}>
        <FormattedMessage id="offerWizard.stepGreyPowerData.title" />
      </Heading>
      <div className={formStyles.inputPair}>
        <Field
          name="information.start"
          labelMsg="offerWizard.StepSomeInformation.start.label"
          component={AccessibleDatepickerInput}
          minDate={new Date()}
        />
        <Field
          name="information.end"
          labelMsg="offerWizard.StepSomeInformation.end.label"
          component={AccessibleDatepickerInput}
          minDate={() => {
            if (values.information?.start) {
              return new Date(values.information.start);
            }
            return new Date();
          }}
        />
      </div>
      <div className={formStyles.inputPair}>
        <Field
          name={fieldName('consumption')}
          component={TextInput}
          labelMsg="offerWizard.stepGreenPowerData.consumption.label"
        />
        <Field
          name={fieldName('numberOfMeterPoints')}
          component={TextInput}
          labelMsg="offerWizard.stepGreenPowerData.numberOfMeterPoints.label"
        />
      </div>
      <div role="alert">
        {isGreyPowerConsumptionTooSmall && (
          <InfoBox>
            <Heading level={4}>
              <FormattedMessage id="offerWizard.stepGreenPowerData.isConsumptionTooSmall.title" />
            </Heading>
            <p className={s.consumptionTooSmallText}>
              <FormattedMessage id="offerWizard.stepGreenPowerData.isConsumptionTooSmall.text1" />{' '}
              <a
                href={linkToVerbundProductPortfolio}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage id="offerWizard.stepGreenPowerData.isConsumptionTooSmall.link" />
              </a>
            </p>
          </InfoBox>
        )}
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <Field
          captionMsg="offerWizard.stepGreenPowerData.controlArea"
          name={fieldName('controlArea')}
          options={controlAreaOptions}
          component={RadioGroupInput}
          radioButtonsWrapperClass={s.controlAreaRadios}
        />
        {isControlAreaCustom && (
          <div className={formStyles.inputPairSingle}>
            <Field
              name={fieldName('customControlArea')}
              component={TextInput}
              labelMsg="offerWizard.stepGreenPowerData.controlArea.otherTextInput"
              required
            />
          </div>
        )}
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <Field
          captionMsg="offerWizard.stepGreenPowerData.product"
          name={fieldName('product')}
          options={productOptions}
          component={RadioGroupInput}
          radioButtonsWrapperClass={s.productRadios}
        />
      </div>
    </>
  );
}

export default StepGreyPowerData;
